import {
  Avatar,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import MDBox from "components/MDBox";
import { LojaDataGrid } from "layouts/pages/loja/common";
import { normalizeExternalUrl } from "utils/normalizeExternalUrl";
import { IPatrocinador } from "interfaces/patrocinador";
import { PATROCINADORES_ACCENT } from "../patrocinadores.theme";

const ROW_HEIGHT = 68;

interface Props {
  rows: IPatrocinador[];
  loading?: boolean;
  onEdit: (row: IPatrocinador) => void;
  onDelete: (row: IPatrocinador) => void;
}

export default function TablePatrocinador({
  rows,
  loading,
  onEdit,
  onDelete,
}: Props): JSX.Element {
  const columns: GridColDef<IPatrocinador>[] = [
    {
      field: "urlLogo",
      headerName: "Logo",
      width: 80,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <MDBox display="flex" alignItems="center" justifyContent="center" height="100%" width="100%">
          <Avatar
            src={params.row.urlLogo}
            alt={params.row.nome}
            variant="rounded"
            sx={{ width: 44, height: 44 }}
          />
        </MDBox>
      ),
    },
    {
      field: "nome",
      headerName: "Nome",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={700} noWrap title={params.row.nome}>
          {params.row.nome}
        </Typography>
      ),
    },
    {
      field: "descricao",
      headerName: "Descrição",
      flex: 1,
      minWidth: 180,
      sortable: false,
      renderCell: (params) => (
        <Tooltip title={params.row.descricao || ""}>
          <Typography
            variant="body2"
            color={params.row.descricao ? "text.primary" : "text.secondary"}
            noWrap
            sx={{ maxWidth: "100%" }}
          >
            {params.row.descricao || "Sem descrição"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "urlSite",
      headerName: "Site",
      width: 180,
      renderCell: (params) => {
        const href = normalizeExternalUrl(params.row.urlSite);
        return href ? (
          <Typography
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            variant="body2"
            noWrap
            sx={{ color: PATROCINADORES_ACCENT, maxWidth: "100%" }}
          >
            {params.row.urlSite}
          </Typography>
        ) : (
          "—"
        );
      },
    },
    {
      field: "ordem",
      headerName: "Ordem",
      width: 80,
      type: "number",
      align: "center",
      headerAlign: "center",
    },
    {
      field: "ativo",
      headerName: "Ativo",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.row.ativo ? "Ativo" : "Inativo"}
          color={params.row.ativo ? "success" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "exibirHome",
      headerName: "Home",
      width: 90,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.row.exibirHome ? "Sim" : "Não"}
          color={params.row.exibirHome ? "info" : "default"}
          size="small"
        />
      ),
    },
    {
      field: "tipo",
      headerName: "Tipo",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const home = params.row.exibirHome;
        const eventos = params.row.eventos ?? [];
        const temEvento = eventos.length > 0;
        if (home && temEvento) {
          return <Chip label="Home + Evento" color="secondary" size="small" />;
        }
        if (home) return <Chip label="Somente Home" color="info" size="small" />;
        if (temEvento) return <Chip label="Somente Evento" color="warning" size="small" />;
        return <Chip label="Sem vínculo" color="default" size="small" />;
      },
    },
    {
      field: "eventos",
      headerName: "Eventos",
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const eventos = params.row.eventos ?? [];
        if (!eventos.length) {
          return (
            <Typography variant="caption" color="text.secondary">
              —
            </Typography>
          );
        }
        const visible = eventos.slice(0, 2);
        const extra = eventos.length - visible.length;
        return (
          <Stack direction="row" spacing={0.5} flexWrap="wrap" alignItems="center">
            {visible.map((e) => (
              <Chip key={e.eventoId} label={e.evento.titulo} size="small" />
            ))}
            {extra > 0 && (
              <Tooltip
                title={eventos
                  .slice(2)
                  .map((e) => e.evento.titulo)
                  .join(", ")}
              >
                <Chip label={`+${extra}`} size="small" />
              </Tooltip>
            )}
          </Stack>
        );
      },
    },
    {
      field: "actions",
      headerName: "",
      width: 100,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <Tooltip title="Editar patrocinador">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(params.row);
              }}
            >
              <EditOutlinedIcon fontSize="small" sx={{ color: PATROCINADORES_ACCENT }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Excluir patrocinador">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(params.row);
              }}
            >
              <DeleteOutlineIcon fontSize="small" color="error" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  return (
    <LojaDataGrid
      rows={rows}
      columns={columns}
      loading={loading}
      rowHeight={ROW_HEIGHT}
      onRowClick={(params) => onEdit(params.row)}
      initialState={{
        pagination: { paginationModel: { page: 0, pageSize: 10 } },
        sorting: { sortModel: [{ field: "ordem", sort: "asc" }] },
      }}
      localeText={{ noRowsLabel: "Nenhum patrocinador cadastrado" }}
      sx={{
        border: "1px solid",
        borderColor: "grey.200",
        borderRadius: 2,
        "& .MuiDataGrid-row": { cursor: "pointer" },
        "& .MuiDataGrid-row:hover": {
          bgcolor: "rgba(249, 96, 48, 0.04)",
        },
        "& .MuiDataGrid-columnHeaders": {
          bgcolor: "grey.100",
          fontWeight: 700,
        },
        "& .MuiDataGrid-cell": {
          display: "flex",
          alignItems: "center",
        },
      }}
    />
  );
}
